// A premium, multi-page guided student tour. It lives ABOVE the router (mounted
// once in App), so the frosted spotlight persists across navigation and glides
// from page to page: Dashboard -> Lessons -> Practice -> Mock tests -> Progress.
//
// Pure CSS (no framer-motion / gsap): four backdrop-blur "shutter" panels frame a
// crisp hole that morphs with a spring; an indigo glow-ring breathes on it; a
// frosted tooltip rises in. After each navigation we rAF-poll for the step's
// target (page may still be fetching), then write its rect into state so the
// shutters glide onto it. Three-tier fallback per step: hero selector -> sidebar
// nav testid (always present) -> centered card.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApp } from '@/lib/store';
import { localizedStep } from '@/lib/tourI18n';

const PAD = 10;
const TIP_W = 340;
const TIP_H = 240; // estimate used for clamping/placement
const SEEN_KEY = 'qt_seen_v1';

export const tourSeen = () => {
  try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return true; }
};
const markSeen = () => { try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ } };

// Module-scoped trigger so any page (e.g. the dashboard "Quick tour" button) can
// relaunch without prop-drilling. QuickTour registers its start() on mount.
let _start = null;
export function startTour() { if (_start) _start(); }

// A real lesson (and its worked walkthrough) per exam to drive the tour into.
// Both demo units have an authored walkthrough, so the dive never dead-ends.
// The same task drives the whole deep dive: the lesson, its walkthrough, and the
// practice drill are all this one task type, so the flow stays consistent.
const DEMO = {
  celpip: { unit: 'celpip-r-p1-strategy', wt: 'wt_celpip_r_p1_purpose', task: 'reading_p1_correspondence' },
  pte: { unit: 'pte_r_unit_rw_fib', wt: 'wt_pte_rw_fib', task: 'rw_fib' },
};
// Replace :exam / :unit / :wt / :task in a route or selector string.
const sub = (str, exam) => (str || '')
  .replace(':exam', exam)
  .replace(':unit', DEMO[exam]?.unit || '')
  .replace(':wt', DEMO[exam]?.wt || '')
  .replace(':task', DEMO[exam]?.task || '');

const STEPS = [
  { id: 'welcome', kind: 'modal', route: '/', eyebrow: 'A 90-second tour', title: "Here's how you'll prepare",
    body: "Here's the loop you'll run: set a target, learn a task properly, watch it worked through, drill it on the clock, then sit a full test. It takes a minute, and you can leave any time.",
    cta: 'Show me', secondary: 'Not now' },

  { id: 'next', kind: 'spotlight', route: '/', target: '[data-testid="next-action"]', fallback: '[data-testid="nav-dashboard"]', scroll: 'center', placement: 'auto',
    eyebrow: 'Step 1 · Your target', title: 'It tells you what to do next',
    body: "Pick the band you're aiming for and this card reads your coverage and hands you the single task that lifts your score the most right now. No guessing where to start." },

  { id: 'coverage', kind: 'spotlight', route: '/', target: '[data-testid="coverage-map"]', fallback: '[data-testid="nav-dashboard"]', scroll: 'center', placement: 'auto',
    eyebrow: 'Step 1 · Your target', title: 'Your whole exam, mapped',
    body: 'Every task in reading, listening, writing and speaking, with how far through each one you are. Green is solid, the gaps are the plan.' },

  // ── Lessons: organised by skill, then INTO a real lesson ──
  { id: 'lessons-skills', kind: 'spotlight', route: '/:exam/lessons', target: '[data-testid="lessons-hub-hero"]', fallback: '[data-testid="nav-lessons"]', scroll: 'center', placement: 'below',
    eyebrow: 'Step 2 · Learn the method', title: 'Organised by the four skills',
    body: "Your lessons are grouped by listening, reading, writing and speaking, and every task type in the exam has its own. Let's open one and look inside." },

  { id: 'lesson-pick', kind: 'spotlight', route: '/:exam/lessons', target: '[data-testid="lesson-:unit"]', fallback: '[data-testid="nav-lessons"]', scroll: 'center', placement: 'auto',
    eyebrow: 'Step 2 · Learn the method', title: 'Open a lesson',
    body: "Click any task type to open its lesson. Here's one." },

  { id: 'lesson-body', kind: 'spotlight', route: '/:exam/lessons/:unit', pin: 'br',
    eyebrow: 'Step 2 · Inside a lesson', title: 'Scroll through the lesson',
    body: 'Inside, the whole task is broken down: the method, then every trap the exam builds in to catch you, each card opening for more. Take your time and scroll as far as you like.' },

  { id: 'lesson-walkthrough-cta', kind: 'spotlight', route: '/:exam/lessons/:unit', target: '[data-testid="lesson-walkthrough-cta"]', fallback: '[data-testid="nav-lessons"]', scroll: 'center', placement: 'above',
    eyebrow: 'Step 2 · Inside a lesson', title: 'Then watch it worked through',
    body: "Before you try it yourself, you watch a real item solved one move at a time. Let's open it." },

  { id: 'walkthrough-player', kind: 'spotlight', route: '/:exam/walkthrough/:wt', pin: 'bl', target: '[data-testid="wt-controls"]', scroll: 'nearest',
    advance: '[data-testid="wt-next"]',
    eyebrow: 'Step 2 · Worked example', title: 'See it solved, step by step',
    body: 'Press Next to reveal each move: the words light up and the note above explains why, until the right answer turns green. Step all the way through, then keep going.' },

  // ── Practice ──
  { id: 'practice', kind: 'spotlight', route: '/:exam/practice?skill=reading', target: 'a[data-testid="practice-type-:task"]', fallback: '[data-testid="nav-practice"]', scroll: 'center', placement: 'below',
    eyebrow: 'Step 3 · Practise it', title: 'Now drill that same task',
    body: 'Here is the task you just learned, ready as a timed set under the real exam timing. Every wrong answer comes back with the trap explained, so it never catches you twice. Every task type has its own.' },

  { id: 'mocks', kind: 'spotlight', route: '/:exam/mocks', target: '[data-testid="mock-featured"]', fallback: '[data-testid="nav-mock-tests"]', scroll: 'center', placement: 'auto',
    eyebrow: 'Step 4 · Sit a full test', title: 'The real thing, end to end',
    body: 'When the parts click, put them together: a full timed sitting in the real order. Reading and listening score instantly; writing and speaking get AI feedback. "Walk through the exam" up here shows you the real screen first.' },

  { id: 'progress', kind: 'spotlight', route: '/:exam/progress', target: '[data-testid="progress-hero"]', fallback: '[data-testid="nav-progress"]', scroll: 'center', placement: 'below',
    eyebrow: 'Step 5 · See where you stand', title: 'Honest numbers, and the fix',
    body: 'Everything feeds back here: your weakest tasks ranked by the points they cost, each linked to the lesson or drill that closes it. This is the loop you run to your target.' },

  { id: 'finish', kind: 'modal', route: '/:exam/lessons', eyebrow: "That's the loop", title: 'Start where it counts',
    body: 'Learn a method, watch it worked through, drill it, sit a test, see where you stand, repeat. The fastest way in is your first lesson.',
    cta: 'Open my first lesson', secondary: "I'll look around myself", action: 'first-lesson' },
];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));
const isVisible = (el) => { const r = el.getBoundingClientRect(); return r.width > 1 && r.height > 1; };

function computePlacement(r, pref) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const fitsRight = r.left + r.width + 22 + TIP_W < vw;
  const fitsBelow = r.top + r.height + 20 + TIP_H < vh;
  const fitsAbove = r.top - 20 - TIP_H > 0;
  if (pref === 'right' && fitsRight) return 'right';
  if (pref === 'below' && fitsBelow) return 'below';
  if (pref === 'above' && fitsAbove) return 'above';
  if (fitsRight) return 'right';
  if (fitsBelow) return 'below';
  if (fitsAbove) return 'above';
  return 'center';
}

function tipPosition(r, placement) {
  const vw = window.innerWidth, vh = window.innerHeight, M = 16;
  let top, left;
  if (placement === 'right') { left = r.left + r.width + 18; top = r.top + r.height / 2 - TIP_H / 2; }
  else if (placement === 'below') { top = r.top + r.height + 18; left = r.left + r.width / 2 - TIP_W / 2; }
  else if (placement === 'above') { top = r.top - 18 - TIP_H; left = r.left + r.width / 2 - TIP_W / 2; }
  else { left = vw / 2 - TIP_W / 2; top = clamp(r.top + r.height + 18, M, vh - TIP_H - M); }
  return { top: clamp(top, M, vh - TIP_H - M), left: clamp(left, M, vw - TIP_W - M) };
}

function caret(r, placement, tip) {
  if (placement === 'right') return { side: 'left', top: clamp(r.top + r.height / 2 - tip.top - 7, 14, TIP_H - 22) };
  if (placement === 'below') return { side: 'top', left: clamp(r.left + r.width / 2 - tip.left - 7, 14, TIP_W - 22) };
  if (placement === 'above') return { side: 'bottom', left: clamp(r.left + r.width / 2 - tip.left - 7, 14, TIP_W - 22) };
  return null;
}

// Poll for a step's target after navigation (page may still be fetching); degrade
// hero -> nav -> center. Uses setTimeout, not requestAnimationFrame: rAF is paused
// in background/headless contexts, which would stall the whole tour.
function resolveTarget(step, onRect, onCenter) {
  const deadline = performance.now() + 2600;
  let timer = 0;
  let done = false;
  const finish = (el) => {
    try { el.scrollIntoView({ block: step.scroll || 'center', inline: 'nearest' }); } catch { /* noop */ }
    timer = setTimeout(() => { if (!done) onRect(el.getBoundingClientRect(), el); }, 60); // let scroll settle, then measure
  };
  const poll = () => {
    if (done) return;
    // Wait for the REAL target first. The sidebar nav fallback is always present, so
    // checking it on the same tick would let it win the race while the page is still
    // fetching its hero. Only fall back to it once the target has truly not shown up.
    const el = document.querySelector(step.target);
    if (el && isVisible(el)) { finish(el); return; }
    if (performance.now() < deadline) { timer = setTimeout(poll, 60); return; }
    const fb = step.fallback && document.querySelector(step.fallback);
    if (fb && isVisible(fb)) { finish(fb); return; }
    onCenter();
  };
  poll();
  return () => { done = true; clearTimeout(timer); };
}

export default function QuickTour({ exam = 'celpip' }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Instructional language state. The tour text switches with it, so a
  // student who flipped the switcher to zh/ko/es/ar/fa sees the tour in
  // the same language as their lessons. Structural fields (route/target/pin)
  // are code-owned; only display strings come from the overlay.
  const { lang } = useApp();

  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null); // null => centered (modal / unresolved)
  const [placement, setPlacement] = useState('auto');
  const [moving, setMoving] = useState(false);
  const [tracking, setTracking] = useState(false);

  const elRef = useRef(null);
  const stepRef = useRef(STEPS[0]);
  const cancelRef = useRef(null);
  const moveTimer = useRef(0);
  const trackTimer = useRef(0);
  const originRef = useRef(null);

  // Language overlay applied per-step. Structural fields (route/target/pin/
  // advance/kind/action) stay from STEPS; display fields (eyebrow/title/
  // body/cta/secondary) get replaced when a translation is available.
  const step = localizedStep(lang, STEPS[i]);
  stepRef.current = step;
  const resolveRoute = useCallback((tpl) => sub(tpl || '/', exam), [exam]);

  const start = useCallback(() => { originRef.current = document.activeElement; setI(0); setRect(null); setOpen(true); }, []);

  // register the relaunch trigger. The tour is OPT-IN only: it never auto-launches
  // in the student's face on entry. They start it from "Take a tour" in the sidebar.
  useEffect(() => { _start = start; return () => { if (_start === start) _start = null; }; }, [start]);

  const teardown = useCallback(() => {
    if (cancelRef.current) cancelRef.current();
    cancelRef.current = null;
    clearTimeout(moveTimer.current);
    clearTimeout(trackTimer.current);
    elRef.current = null;
    setOpen(false); setRect(null); setMoving(false); setI(0);
    if (originRef.current && originRef.current.focus) originRef.current.focus();
  }, []);

  const dismiss = useCallback(() => { markSeen(); teardown(); }, [teardown]);
  const next = useCallback(() => setI((v) => Math.min(STEPS.length - 1, v + 1)), []);
  const back = useCallback(() => setI((v) => Math.max(0, v - 1)), []);

  // On a step with `advance`, the tour's Next first steps that control (e.g. the
  // walkthrough's own Next) so the student moves through it themselves; only once
  // that control is gone (the walkthrough hit its last step) does Next move the tour
  // on. This keeps the tour from skipping past the worked example.
  const advanceOrNext = useCallback(() => {
    const sel = STEPS[i]?.advance;
    if (sel) { const el = document.querySelector(sel); if (el && !el.disabled) { el.click(); return; } }
    next();
  }, [i, next]);

  const openFirstLesson = useCallback(() => {
    const card = document.querySelector('a[data-testid^="lesson-"]');
    const href = card && card.getAttribute('href');
    markSeen(); teardown();
    navigate(href || resolveRoute('/:exam/lessons'));
  }, [navigate, resolveRoute, teardown]);

  // Drive each step: navigate if needed, then resolve + glide the spotlight.
  useEffect(() => {
    if (!open) return undefined;
    if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; }

    const targetRoute = resolveRoute(step.route);
    const needNav = step.route && location.pathname !== targetRoute;

    if (step.kind === 'modal') {
      if (needNav) navigate(targetRoute);
      elRef.current = null;
      setMoving(false);
      setRect(null);
      return undefined;
    }

    // Pinned step with no element to ring: navigate, then just show the corner card
    // (no dim, no spotlight) so the student can read and scroll the page freely. Start
    // at the top of the page so they read the lesson from the beginning.
    if (step.pin && !step.target) {
      if (needNav) { navigate(targetRoute); setTimeout(() => window.scrollTo({ top: 0 }), 140); }
      else window.scrollTo({ top: 0 });
      elRef.current = null;
      setMoving(false);
      setRect(null);
      return undefined;
    }

    const applyRect = (r, el) => {
      elRef.current = el;
      setMoving(true);
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      setPlacement(computePlacement(r, step.placement));
      clearTimeout(moveTimer.current);
      moveTimer.current = setTimeout(() => setMoving(false), 480);
    };
    const toCenter = () => { elRef.current = null; setRect(null); setMoving(false); };
    // Resolve :unit / :wt tokens in the selector so steps can target a specific lesson card.
    const rstep = { ...step, target: sub(step.target, exam), fallback: step.fallback };

    if (needNav) {
      setMoving(true); // hold the frost through the route swap; do NOT clear the old hole
      navigate(targetRoute);
      const t = setTimeout(() => { cancelRef.current = resolveTarget(rstep, applyRect, toCenter); }, 60);
      return () => clearTimeout(t);
    }
    cancelRef.current = resolveTarget(rstep, applyRect, toCenter);
    return undefined;
  }, [i, open]);

  // Track the target through scroll / resize (instant, no glide lag).
  useEffect(() => {
    if (!open) return undefined;
    const remeasure = () => {
      const el = elRef.current;
      if (!el) return;
      setTracking(true);
      clearTimeout(trackTimer.current);
      trackTimer.current = setTimeout(() => setTracking(false), 160);
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      setPlacement(computePlacement(r, stepRef.current.placement));
    };
    window.addEventListener('resize', remeasure, { passive: true });
    window.addEventListener('scroll', remeasure, { passive: true, capture: true });
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, { capture: true });
    };
  }, [open]);

  // Keyboard
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
      else if (e.key === 'ArrowRight') advanceOrNext();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss, advanceOrNext, back]);

  useEffect(() => () => { if (cancelRef.current) cancelRef.current(); }, []);

  if (!open) return null;

  const centered = !rect;
  const isModal = step.kind === 'modal';
  const pinned = !!step.pin;
  const t = rect ? rect.top - PAD : 0;
  const l = rect ? rect.left - PAD : 0;
  const w = rect ? rect.width + PAD * 2 : 0;
  const h = rect ? rect.height + PAD * 2 : 0;
  const tip = rect && !isModal ? tipPosition(rect, placement) : null;
  const car = rect && !isModal ? caret(rect, placement, tip) : null;
  const spotlightIdx = STEPS.filter((s) => s.kind === 'spotlight');
  const dotActive = spotlightIdx.indexOf(step);

  return createPortal(
    <div
      className={`qt-root${moving ? ' qt-moving' : ''}${tracking ? ' qt-track' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Guided tour"
    >
      {/* Full dim only behind the centered welcome/finish modal. NOT during a glide
          (the shutters already dim at a constant 0.34) and NOT on pinned steps, which
          leave the page fully visible so the student can read and scroll. */}
      {centered && !pinned && <div className="qt-veil" />}

      {/* Dimming shutters only for normal spotlight steps. Pinned steps skip them. */}
      {!centered && !pinned && (
        <>
          <div className="qt-shutter" style={{ top: 0, left: 0, width: '100vw', height: Math.max(0, t) }} />
          <div className="qt-shutter" style={{ top: t + h, left: 0, width: '100vw', height: `calc(100vh - ${t + h}px)` }} />
          <div className="qt-shutter" style={{ top: Math.max(0, t), left: 0, width: Math.max(0, l), height: h }} />
          <div className="qt-shutter" style={{ top: Math.max(0, t), left: l + w, width: `calc(100vw - ${l + w}px)`, height: h }} />
        </>
      )}

      {/* The glow-ring renders for both spotlight and pinned steps (a soft highlight
          with no surrounding dim on pinned steps). */}
      {!centered && <div className="qt-ring" style={{ top: t, left: l, width: w, height: h }} />}

      <div
        key={step.id}
        className={isModal ? 'qt-modal' : pinned ? `qt-tip qt-pin qt-pin-${step.pin}` : 'qt-tip'}
        style={isModal || pinned ? undefined : tip}
        data-placement={pinned ? 'pin' : placement}
      >
        {car && !pinned && <span className="qt-caret" data-side={car.side} style={{ top: car.top, left: car.left }} />}

        <p className="qt-eyebrow mono-stat">{step.eyebrow}</p>
        <h3 className="qt-title font-display">{step.title}</h3>
        <p className="qt-body">{step.body}</p>

        {isModal ? (
          <div className="qt-controls">
            {step.secondary && (
              <button type="button" className="cta-quiet" onClick={dismiss}>{step.secondary}</button>
            )}
            <span className="qt-spacer" />
            <button
              type="button"
              className="cta"
              data-testid="tour-next"
              onClick={() => (step.action === 'first-lesson' ? openFirstLesson() : next())}
            >
              {step.cta}
            </button>
          </div>
        ) : (
          <div className="qt-controls">
            <div className="qt-dots">
              {spotlightIdx.map((s, k) => (
                <span key={s.id} className={`qt-dot${k === dotActive ? ' is-active' : ''}`} />
              ))}
            </div>
            {i > 0 && (
              <button type="button" className="cta-quiet" onClick={back}>Back</button>
            )}
            <button type="button" className="cta" data-testid="tour-next" onClick={advanceOrNext}>Next</button>
          </div>
        )}

        <button type="button" className="qt-close" aria-label="Close tour" data-testid="tour-skip" onClick={dismiss}>×</button>
      </div>
    </div>,
    document.body,
  );
}
